import React, { useEffect, useRef, useState } from 'react';
import './Button.css';
import './FeedbackDialog.css';
import { submitFeedback } from '../firebase';
import { trackEvent } from '../analytics';

// Must match MAX_MESSAGE_CHARS in packages/functions/feedback/api/main.py.
// The callable is the real gate; this only keeps the user from typing a note
// the server is going to refuse.
export const MAX_MESSAGE_LENGTH = 5000;

// Codes whose .message is a curated sentence written by our own Python
// handler (packages/functions/feedback/api/main.py) — safe to show verbatim.
// Everything else (notably functions/internal and functions/unauthenticated)
// arrives with .message set to the bare error CODE by the Functions SDK on
// transport failures / expired tokens, so those fall back to generic copy.
const FRIENDLY_ERROR_CODES = new Set(['functions/invalid-argument', 'functions/resource-exhausted']);

// Free-text feedback, opened from the sidebar footer button. Chrome mirrors the
// other Studio dialogs (`.dialog-overlay` + a #1c1f26 panel, Escape and
// overlay-click dismiss) so it reads as part of the same family.
//
// `page` and `projectId` are passed in rather than read from react-router here,
// so the dialog stays router-free and renders standalone in tests.
const FeedbackDialog = ({ onClose, page = null, projectId = null }) => {
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState(null);
  const textareaRef = useRef(null);

  useEffect(() => {
    trackEvent('feedback_opened', { page });
    // Fires once per open: the dialog is conditionally mounted by Studio, so
    // mount IS the open. `page` is intentionally not a dependency — a route
    // change under an open dialog is not a second open.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (textareaRef.current) textareaRef.current.focus();
  }, []);

  // Every dismissal path is inert while a submit is in flight. The request is
  // deliberately not retried, so a dialog that vanishes mid-send leaves the
  // user with neither the confirmation nor the error — and no typed text to
  // resend. Cancel and × are disabled; Escape and overlay-click no-op here.
  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === 'Escape' && !submitting) onClose();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [onClose, submitting]);

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget && !submitting) onClose();
  };

  const trimmed = message.trim();
  const canSend = trimmed.length > 0 && !submitting;

  const handleSubmit = async () => {
    if (!canSend) return;
    setSubmitting(true);
    setError(null);
    try {
      // Deliberately NOT wrapped in withRetry: the callable is a plain write
      // with no idempotency key, so an auto-retry on a timed-out-but-delivered
      // request would file the same note twice. A failure keeps the typed text
      // on screen and lets the user decide to retry.
      await submitFeedback({
        message: trimmed,
        page,
        projectId,
        appVersion: process.env.REACT_APP_VERSION || null,
        userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
      });
      trackEvent('feedback_submitted', { page });
      setSent(true);
    } catch (err) {
      console.error('Error submitting feedback:', err);
      // functions/unauthenticated (expired session) is handled separately:
      // whether .message is our curated sentence or the bare code
      // "unauthenticated" depends on whether the request reached our handler,
      // so the copy is fixed here instead of trusting .message for this code.
      setError(
        err?.code === 'functions/unauthenticated'
          ? 'You need to sign in again to send feedback.'
          : FRIENDLY_ERROR_CODES.has(err?.code) && err?.message
            ? err.message
            : "We couldn't send that just now. Please try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="dialog-overlay"
      onClick={handleOverlayClick}
      data-testid="feedback-dialog-overlay"
    >
      <div
        className="feedback-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="feedback-dialog-title"
      >
        <button
          type="button"
          className="feedback-dialog__close"
          onClick={onClose}
          disabled={submitting}
          aria-label="Close"
        >
          ×
        </button>

        {sent ? (
          <div className="feedback-dialog__success">
            <div className="feedback-dialog__check" aria-hidden="true">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M4 10.5l4 4 8-9" />
              </svg>
            </div>
            <h2 id="feedback-dialog-title">Thanks — got it.</h2>
            <p className="feedback-dialog__success-note">
              We read every note. If it needs a reply, we'll email you at the
              address on your account.
            </p>
            <div className="feedback-dialog__actions feedback-dialog__actions--centered">
              <button type="button" className="btn btn-teal" onClick={onClose}>
                Done
              </button>
            </div>
          </div>
        ) : (
          <>
            <h2 id="feedback-dialog-title">Send feedback</h2>
            <p className="feedback-dialog__intro">
              Found a bug? Wish Tweedia Studio did something it doesn't?
              Tell us — we read every note.
            </p>
            <textarea
              ref={textareaRef}
              className="feedback-dialog__textarea"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              maxLength={MAX_MESSAGE_LENGTH}
              rows={5}
              disabled={submitting}
              aria-label="Your feedback"
              placeholder="What happened, or what would you like Tweedia Studio to do?"
            />
            <p className="feedback-dialog__disclosure">
              Your account, current page, and app version are included
              automatically so we can follow up.
            </p>
            {error && (
              <p className="feedback-dialog__error" role="alert">
                {error}
              </p>
            )}
            <div className="feedback-dialog__actions">
              <button
                type="button"
                className="feedback-dialog__cancel"
                onClick={onClose}
                disabled={submitting}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-teal"
                onClick={handleSubmit}
                disabled={!canSend}
              >
                {submitting ? 'Sending…' : 'Send feedback'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default FeedbackDialog;
